import { useEffect, useState } from 'react';
import type { Role } from '../types/Role';
import { getRoles } from '../api/roles';

// Module-level cache (once per session). Cleared on logout via
// clearRolesCache(). Mirrors useFundTypes / usePages. Exposes only ACTIVE
// roles — used to populate the Users page role-assignment editor.
let cache: Role[] | null = null;
let inflight: Promise<Role[]> | null = null;

function loadRoles(): Promise<Role[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getRoles()
    .then((d) => {
      cache = d;
      inflight = null;
      return d;
    })
    .catch((e: unknown) => {
      inflight = null;
      throw e;
    });
  return inflight;
}

export function clearRolesCache(): void {
  cache = null;
  inflight = null;
}

interface UseRolesResult {
  roles: Role[] | null;
  loading: boolean;
  error: string | null;
}

function activeOnly(roles: Role[] | null): Role[] | null {
  return roles ? roles.filter((r) => r.isActive) : null;
}

export function useRoles(): UseRolesResult {
  const [state, setState] = useState<UseRolesResult>(() =>
    cache
      ? { roles: activeOnly(cache), loading: false, error: null }
      : { roles: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.roles && prev.roles.length === activeOnly(cache)?.length
          ? prev
          : { roles: activeOnly(cache), loading: false, error: null },
      );
      return;
    }
    let cancelled = false;
    loadRoles()
      .then((d) => {
        if (!cancelled)
          setState({ roles: activeOnly(d), loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({
            roles: null,
            loading: false,
            error: e instanceof Error ? e.message : String(e),
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
